//@flow
import * as React from 'react';
import AcknowledgeButton from 'app/common/react/components/growth/unauth/signup/AccountRecovery/AcknowledgeButton';
import i18n from 'app/common/lib/i18n';
import Modal from 'app/common/react/ui/Modal';
import ResetPasswordButton from 'app/common/react/components/growth/unauth/signup/AccountRecovery/ResetPasswordButton';
import templateConst from 'app/common/lib/templateConst';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { Box, Link, Text } from 'gestalt';

const SOURCE_PARAM = '?source=account_recovery';

type Props = {|
  email: string,
  onClose: () => void,
  onPasswordResetSent: () => void,
  socialButtons: React.Node,
|};

export default function PasswordResetModal({
  email,
  onClose,
  onPasswordResetSent,
  socialButtons,
}: Props) {
  const resetSentHeading = i18n._(
    'Password reset email sent',
    'Modal label for confirmation of password reset send',
  );
  return (
    <Modal
      accessibilityCloseLabel={i18n._('close', 'accessible label for button to close modal')}
      accessibilityModalLabel={resetSentHeading}
      heading={resetSentHeading}
      onDismiss={onClose}
      role="alertdialog"
      size="md"
    >
      <Box paddingX={6} marginBottom={4}>
        <Text overflow="normal">
          {interpolateNamedTemplate(
            i18n._(
              'We sent instructions to pick a new password to {{ email }}.',
              'Statement that a password reset email has been sent to the user',
            ),
            {
              email: (
                <Text inline key="emailAddress" overflow="normal" weight="bold">
                  {email}
                </Text>
              ),
            },
          )}
        </Text>
        <Box paddingY={4}>
          <ResetPasswordButton
            inline
            color="gray"
            email={email}
            callBack={onPasswordResetSent}
            overrideText={i18n._(
              'Resend email',
              'Button to allow the user to request a password reset email',
            )}
          />
        </Box>
        <Box>
          <Box marginBottom={4}>
            <Text>
              {interpolateNamedTemplate(
                i18n._(
                  'Didn’t get the email? Try these {{ tips }}',
                  'Direction to help center if the password reset email failed',
                ),
                {
                  tips: (
                    <Link
                      inline
                      key="helpCenterLink"
                      target="blank"
                      href={templateConst.settings.HELP_PASSWORD_RESET_URL + SOURCE_PARAM}
                    >
                      <Text inline key="helpCenter" weight="bold">
                        {i18n._('tips from our Help Center', 'Link text to go to help center')}
                      </Text>
                    </Link>
                  ),
                },
              )}
            </Text>
          </Box>
          <Box marginBottom={4}>
            <Text>
              {interpolateNamedTemplate(
                i18n._(
                  'Not your email address? {{ tryAgain }}',
                  'Link to password reset flow for users who make an incorrect password reset request',
                ),
                {
                  tryAgain: (
                    <Link
                      inline
                      key="passwordResetLink"
                      href={templateConst.settings.PASSWORD_RESET_URL}
                    >
                      <Text inline key="tryAgain" weight="bold">
                        {i18n._(
                          'Try another',
                          'Link text to enter the password reset flow for a different email address/username',
                        )}
                      </Text>
                    </Link>
                  ),
                },
              )}
            </Text>
          </Box>
        </Box>
        <Box>
          <AcknowledgeButton color="red" onClick={onClose} />
        </Box>
      </Box>
    </Modal>
  );
}
